import { Box, CircularProgress } from '@mui/material';
import { useState } from 'react';
import {
  RoundedButton,
  SearchInputWrapper,
  SectionContainerWrapper
} from './styled';
import DropdownSelect from '../../components/DropdownSelect';
import Icons from '../../components/icons';
import {
  academicYears,
  defaultAcademicYear
} from '../../constants/academicYears';
import { SnackbarSeverityType } from '../../enums';
import ToastMessage from '../../components/ToastMessage';
import ButtonTabSwitch from '../../components/ButtonTabSwitch';
import { concessionTypeTabs } from './utils/constant';
import { ConcessionType, PaymentConcessionMasterProps } from './types';
import FullPaymentConcessionTable from './components/FullPaymentConcessionTable';
import AddNewGroupModal from './components/AddNewGroupModal';
import paymentConcessionMaster from '../../apis/payments/paymentConcessionMaster';
import { useSchoolClassesConfigHook } from '../common/hooks/useSchoolClassesConfig';
import { flatMap } from 'lodash';
import { ButtonContainer } from '../Staff/styled';
import { PageWrapper, Title } from '../common/styled';

const FullPaymentConcessionPage = () => {
  const { loggedInUser, gradeOptions } = useSchoolClassesConfigHook();
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(
    defaultAcademicYear || ''
  );
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [activeTab, setActiveTab] = useState<ConcessionType>('rollover');
  const [isAddNewGroupModalOpen, setIsAddNewGroupModalOpen] = useState(false);

  const [paymentConcessionMasterData, setPaymentConcessionMasterData] =
    useState<PaymentConcessionMasterProps[]>();

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const fetchPaymentConcessionMaster = async () => {
    const response = await paymentConcessionMaster.fetchPaymentConcessionMaster(
      {
        school: loggedInUser?.school,
        academicYear: selectedAcademicYear
      }
    );
    setPaymentConcessionMasterData(
      response as unknown as PaymentConcessionMasterProps[]
    );
  };

  const handleSearch = async () => {
    setIsSearching(true);
    try {
      await fetchPaymentConcessionMaster();
      showToast(
        'Successfully fetched payment concession master',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error?.errors?.[0].message ||
          'Error fetching payment concession master',
        SnackbarSeverityType.error
      );
    } finally {
      setIsSearching(false);
      setHasSearched(true);
    }
  };

  const academicYearOptions = academicYears.filter(
    (academicYear) => academicYear.value !== '2024-2025'
  );

  const allPaymentConcessionMasterGrades = flatMap(
    paymentConcessionMasterData,
    (paymentConcessionMasterData) => paymentConcessionMasterData.grades
  );

  return (
    <Box>
      <Title>Full Payment Concession Configuration</Title>
      <SectionContainerWrapper>
        <SearchInputWrapper>
          <DropdownSelect
            label='Academic Year'
            placeholder='Select Academic Year'
            name='academicYear'
            value={selectedAcademicYear}
            options={academicYearOptions}
            onChange={(event) => {
              setSelectedAcademicYear(event.target.value as string);
              setHasSearched(false);
            }}
            hasTopLabel
          />
          <ButtonContainer>
            <RoundedButton
              sx={{ marginTop: '24px' }}
              onClick={handleSearch}
              disabled={isSearching || !selectedAcademicYear}
            >
              <Icons.SearchWhiteOutlined style={{ marginRight: '6px' }} />
              {isSearching ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Search'
              )}
            </RoundedButton>
          </ButtonContainer>
        </SearchInputWrapper>
      </SectionContainerWrapper>

      {hasSearched && !isSearching && (
        <>
          <SectionContainerWrapper>
            <ButtonTabSwitch
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              tabs={concessionTypeTabs}
            />
          </SectionContainerWrapper>
          <PageWrapper>
            <RoundedButton
              sx={{ alignSelf: 'flex-end' }}
              onClick={() => setIsAddNewGroupModalOpen(true)}
            >
              Add New Group
            </RoundedButton>

            <FullPaymentConcessionTable
              activeTab={activeTab}
              paymentConcessionMasterData={paymentConcessionMasterData}
              gradeOptions={gradeOptions}
              selectedAcademicYear={selectedAcademicYear}
              loggedInUser={loggedInUser}
              fetchPaymentConcessionMaster={fetchPaymentConcessionMaster}
              showToast={showToast}
              allPaymentConcessionMasterGrades={
                allPaymentConcessionMasterGrades
              }
            />
          </PageWrapper>
        </>
      )}

      {isAddNewGroupModalOpen && (
        <AddNewGroupModal
          selectedAcademicYear={selectedAcademicYear}
          isAddNewGroupModalOpen={isAddNewGroupModalOpen}
          setIsAddNewGroupModalOpen={setIsAddNewGroupModalOpen}
          showToast={showToast}
          fetchPaymentConcessionMaster={fetchPaymentConcessionMaster}
          allPaymentConcessionMasterGrades={allPaymentConcessionMasterGrades}
        />
      )}

      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default FullPaymentConcessionPage;
